// ***** Chart component *****
//
// This component allows data (net CSAT score over selected time) to be plotted in a chart.
// It is built using the c3.js library which is built on top of d3.js, that's why d3 and c3 need to be imported.
//
// This component can be imported and it currently is by analytics and dashboard modules to display chart on analytics and dashboard pages.
//
// The component consists of one public function 'initializeChart' which initializes the chart on the html element that is passed in as an argument.
// 'initializeChart' function has three inner functions, two of which handle showing and hiding the tooltip and the third one 'populate', which fills the chart with data when any filter changes.


define(['jquery',
        'moment',
        'd3',
        'c3'],
    function ($, moment, d3, c3) {

        // This variable will hold the data passed in (response from a query) and is needed to provide context for tooltip content (since c3 library doesn't store it)
        var chartData;

        var chartMode;

        function lookupDescriptorFromStart(interval_start) {
            if (chartData) {
                for(var i = 0; i < chartData.length; i++) {
                    var val = chartData[i];
                    var a = moment(val.interval_start);
                    var b = moment(interval_start);
                    if (a.isSame(b)) {
                        if (chartMode == 'weekly') {
                            return a.format('DD MMM YYYY', );
                        } else if (chartMode == 'quarterly') {
                            return 'Q' + a.format('Q YYYY');
                        }
                        return a.format('MMM YYYY');
                    }
                }
            } else {
                return 'n/a';
            }
        }

        // Set of default options to be used when the chart gets initialized.
        var chart_default_options = {
            bindto: null,
            padding: {
                top: 8,
                right: 24
            },
            data: {
                json: '',
                keys: {
                    x: 'interval_start',
                    value: ['net_csat_score']
                },
                xFormat: '%Y-%m-%dT%H:%M:%S%Z',
                types: {
                    net_csat_score: 'line'
                }
            },
            point: {
                r: 5
            },
            color: {
                pattern: ['#00054d']
            },
            axis: {
                x: {
                    type: 'timeseries',
                    tick: {
                        format: function (interval_start) { return lookupDescriptorFromStart(interval_start); }
                    }
                },
                y: {
                    tick: {
                        count: 4,
                        format: function (x) {
                            var round_x = Math.round(x);
                            var lowerThreshold = 100;
                            return Math.min(round_x, lowerThreshold);
                        }
                    },
                    padding: {
                      top: 4,
                      bottom: 0
                    }
                }
            },
            grid: {
                y: {
                    show: true
                }
            }
        };

        function tooltipTitle() {
            return 'Net CSAT Score';
        }

        function tooltipContent(d) {
            var dataJson = chartData;
            var previousIndex = d[0].index - 1;
            var currentIndex = d[0].index;
            var intervalDescriptorRange = moment(dataJson[currentIndex].interval_start).format('D MMM YYYY') +
            ' - ' + moment(dataJson[currentIndex].interval_end).subtract(1, "days").format('D MMM YYYY') ;

            function calculateNetCsatScoreDelta() {
                if (previousIndex < 0) {
                    return 0;
                } else {
                    var currentNetCsatScore = d[0].value;
                    var previousNetCsatScore = dataJson[previousIndex].net_csat_score;
                    return currentNetCsatScore - previousNetCsatScore;
                }
            }

            function positiveOrNegativeDelta(netCsatScoreDelta) {
                if (netCsatScoreDelta > 0) {
                    return '<div class=\'text-success\'>+' + Math.abs(netCsatScoreDelta).toFixed(1) + '</div>';
                } else if (netCsatScoreDelta === 0) {
                    return '<div class=\'text-muted\'>' + Math.abs(netCsatScoreDelta).toFixed(1) + '</div>';
                } else {
                    return '<div class=\'text-danger\'>-' + Math.abs(netCsatScoreDelta).toFixed(1) + '</div>';
                }
            }

            function checkIfFirstDataPoint() {
                if(d[0].value === chartData[0].net_csat_score) {
                    return true;
                } else {
                    return false;
                }
            }

            function showOrHideDelta() {
                if(checkIfFirstDataPoint()) {
                    return '<br>';
                } else {
                    return positiveOrNegativeDelta(calculateNetCsatScoreDelta());
                }
            }

            return "<div class='card shadow'>" +
                   "    <div class='card-body'>" +
                   "        <h3 class='card-title font-weight-normal mb-0'>Net CSAT Score</h3>" +
                   "        <div class='headline-score d-inline-block mt-1'>" + d[0].value.toFixed(1) + "</div>" +
                   "        <div class='float-right ml-3 mt-3'>" +
                   "            <div class='h3 mb-0'>" + showOrHideDelta() + "</div>" +
                   "            <small class='text-muted'>" + intervalDescriptorRange + "</small>" +
                   "        </div>" +
                   "    </div>" +
                   "</div>";
        }

        // Function that emits custom event 'hoverstart' so that analytics module can listen to it and trigger column hover in the table when the tooltip in the chart is shown.
        var onMouseOver = function (d) {
            $(this.element).trigger('hoverStartChart', d.index);
            $(this.element).mouseout(function(){
                 $(this).trigger('hoverEndChart', d.index);
            });
        };

        // Function that emits 'hoverend' event in order to inform the analytics module when a point in the chart is not hovered over.
        var onMouseOut = function (d) {
            $(this.element).trigger('hoverEndChart', d.index)
        };

        // This function initializes the c3 chart on a given selector (first parameter) and with given options (second parameter). The options passed in will extend the default options.
        // This function is an outer function to three closures which is needed in order for the closure to have access to the variable 'chart' (instance of a c3 chart).
        function initializeChart(selector, options) {

            var extended_options = $.extend(true, {}, chart_default_options,
                                            {
                                                bindto: selector,
                                                data: {
                                                    json: [],
                                                    onmouseover: onMouseOver,
                                                    onmouseout: onMouseOut
                                                },
                                                tooltip: {
                                                    format: {
                                                        title: tooltipTitle
                                                    },
                                                    contents: tooltipContent
                                                }
                                            });

            var combined_options = $.extend(true, extended_options, options);

            var chart = c3.generate(combined_options);

            chart.legend.hide();

            function showTooltip(index) {
                chart.tooltip.show({x: moment(chartData[index].interval_start)});
            }

            function hideTooltip() {
                chart.tooltip.show({x: 0});
            }


            // This function is used to populate the created chart with data. It will store the data passed in, in order to keep the context for the tooltip and it will change the format of the ticks depending on what interval was chosen (weekly, monthly or quarterly).
            function populate(data, mode) {
                chartData = data;
                chartMode = mode;

                //chart.internal.loadConfig(tickFormat);

                chart.load({
                    json: data,
                    keys: {
                        x: 'interval_start',
                        value: ['net_csat_score']
                    }
                });
            }

            function refreshChart() {
                chart.flush();
            }

            // Four closures that are publicly available
            return {
                showTooltip: showTooltip,
                hideTooltip: hideTooltip,
                populate: populate,
                refreshChart: refreshChart
            }
        }

        // Public function
        return {
            create: initializeChart
        };
    }
);
